import { axiosClientv1 } from "./apiClient";
import { ApiResponse } from "../Types";

export interface Advertisement {
  id: string;
  title: string;
  type: "product" | "service";
  description?: string | null;
  price?: number | string;
  image_url?: string | null;
  is_active?: number;
  created_at?: string;
  updated_at?: string;
  order_count?: number;
}

export interface AdOrder {
  id: string;
  ad_id: string;
  ad_title?: string;
  ad_type?: string;
  ad_price?: string;
  user_id: string;
  user_name?: string;
  tenant_id?: string;
  tenant_name?: string;
  store_id: string;
  store_name?: string;
  quantity: number;
  notes?: string | null;
  status: "pending" | "completed" | "cancelled";
  created_at: string;
}

export const GetActiveAdvertisement = async (): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/ads/active`);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load advertisement.",
      data: null,
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const GetAdvertisements = async (params?: {
  page?: number;
  limit?: number;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/ads`, { params });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to load ads.",
      data: [],
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const GetAdvertisementDetail = async (payload: {
  ad_id: string;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/ads/detail`, payload);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to load ad details.",
      data: null,
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const CreateAdvertisementOrder = async (payload: {
  ad_id: string;
  quantity: number;
  notes?: string;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/ads/order`, payload);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to place order.",
      data: null,
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const GetTenantAdOrders = async (params?: {
  status?: string;
  page?: number;
  limit?: number;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/ads/orders`, { params });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to load your orders.",
      data: [],
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const UpdateAdOrderStatus = async (payload: {
  id: string;
  status: "pending" | "completed" | "cancelled";
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/ads/order/status`, payload);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to update order status.",
      data: null,
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const GetAdminAdvertisements = async (params?: {
  search?: string;
  type?: string;
  status?: string;
  page?: number;
  limit?: number;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/ads/admin`, { params });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to load ads.",
      data: [],
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const GetAdminAdvertisementOrders = async (params?: {
  search?: string;
  status?: string;
  ad_id?: string;
  page?: number;
  limit?: number;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/ads/admin/orders`, { params });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message ||
        "Failed to load advertisement orders.",
      data: [],
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const CreateAdvertisement = async (
  formData: FormData,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/ads/create`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to create ad.",
      data: null,
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const UpdateAdvertisement = async (
  formData: FormData,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/ads/update`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to update ad.",
      data: null,
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const DeleteAdvertisement = async (payload: {
  id: string;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/ads/delete`, payload);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to delete ad.",
      data: null,
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};
